/** Shared layout style constants -- reusable CSSProperties for common inline patterns. */

import { animations } from '@federa/const/timing-tokens';
import { disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { FLEX_BETWEEN_STYLE, FLEX_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { colors } from '../css-refs.colors.ts';
import { borderRadius, boxShadow, motion, opacity, zIndex } from '../css-refs.effects.ts';
import { divider } from '../css-refs.layout.ts';
import { spacing } from '../css-refs.spacing.ts';
import { typography } from '../css-refs.typography.ts';
import { BORDER_DEFAULT, BORDER_MUTED, TEXT_SM_MEDIUM, cursor } from './style-primitives.ts';
import type { CSSProperties } from 'react';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { FeedbackVariant } from '@federa/types/ui-feedback-variant';

// Both hairline weights are declared ONCE, in `./style-primitives.ts` beside each other; this leaf imports them and
// declares no twin, so every consumer reads one spelling.

export const BORDER_DASHED_MUTED = `${divider.thickness.thin} dashed ${colors.border.muted}`;

export const TRANSITION_FAST = `all ${motion.duration.fast} ${motion.easing.standard}`;

export const TRANSITION_BASE = `all ${motion.duration.base} ${motion.easing.standard}`;

export const TRANSITION_BACKGROUND_COLOR_FAST = `background-color ${motion.duration.fast} ${motion.easing.standard}`;

// DOES NOT SPREAD `TEXT_SM_PRIMARY` below, and the decline is measured rather than an oversight: two of its seven
// members coincide with that run, and the two are here as part of a CONTROL's chrome spec rather than as body text.
// Spreading the run would couple every admin input's type size to a retune of the body copy -- a repaint of every form
// in the fleet from an edit that named none of them. Same ratio and same ground at `FILTER_INPUT`
// (`../admin/split-pane-filter-styles.ts`, 2/9), `ITEM` (`./ActionsDropdown.tsx`, 2/9) and `menuButtonStyles`
// (`../layout/sidebar-constants.ts`, 2/7).
export const INPUT_BASE_STYLE: CSSProperties = {
	...FULL_WIDTH_STYLE,
	padding: spacing[3],
	fontSize: typography.fontSize.sm,
	color: colors.text.primary,
	backgroundColor: colors.background.primary,
	border: BORDER_DEFAULT,
	borderRadius: borderRadius.md,
};

export const FEEDBACK_VARIANT_COLORS: Record<FeedbackVariant, string> = {
	success: colors.semantic.success,
	error: colors.semantic.error,
	warning: colors.semantic.warning,
	info: colors.semantic.info,
};

export const FEEDBACK_VARIANT_TINTS: Record<FeedbackVariant, string> = {
	success: colors.semantic.successTint,
	error: colors.semantic.errorTint,
	warning: colors.semantic.warningTint,
	info: colors.semantic.infoTint,
};

/** Small font, PRIMARY color -- the plain body/value run: a summary value, a data-viewer content line, a notification
 *  text line, a checklist step label. The weightless colour-sibling of {@link TEXT_SM_SECONDARY}, named because six
 *  surfaces in this package had spelled its two members out longhand and two of them (the flow-viewer WHEN value and
 *  the navigator checklist step label) had drifted into byte-identical private consts under different names. */
export const TEXT_SM_PRIMARY: CSSProperties = {
	fontSize: typography.fontSize.sm,
	color: colors.text.primary,
};

// NO `{ ...TEXT_SM_SECONDARY, margin: 0 }` RUNG, declined on a measurement 2026-09-05 rather than left unasked. The
// exact two-member body has THREE carriers tree-wide -- mfe/wishlists' WishlistDetail, and the navigator transactions
// StepHelpStrip and ownership-styles leaves -- against this family's recorded naming bar of FIVE, which
// `TEXT_SM_SECONDARY_NORMAL` below states is "not a lowered bar". Two further grounds outlive the count: ZERO of the
// three sits in `components/`, so a rung here would ship with no local consumer; and `margin: 0` is a RESET rather
// than a spacing decision -- `QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001` excludes it at `isNonGapMarginValue` on the
// stated ground that the surrounding layout owns the distance, so baking it into a preset would hand every future
// adopter a decision this base deliberately declines to make. FALSIFIER: a fifth carrier of the exact body, at least
// one of them in `components/`, clears the bar and the local-consumer objection at once.
export const TEXT_SM_SECONDARY: CSSProperties = {
	fontSize: typography.fontSize.sm,
	color: colors.text.secondary,
};

/** Small font, TERTIARY color -- the de-emphasised small run: an empty-state hint, an incomplete-checklist status, a
 *  panel meta strip. Third member of the {@link TEXT_SM_PRIMARY} / {@link TEXT_SM_SECONDARY} colour family, named
 *  because six surfaces in this package had spelled its two members out longhand and two of them, in ONE leaf
 *  (`components/navigator/steps/review-step-styles.ts`), were byte-identical under different names. */
export const TEXT_SM_TERTIARY: CSSProperties = {
	fontSize: typography.fontSize.sm,
	color: colors.text.tertiary,
};

/** {@link TEXT_SM_TERTIARY} CENTRED -- the de-emphasised status line a surface draws in place of its content: an empty
 *  state, a loading line, a "nothing here yet" hint. `padding` is deliberately NOT a member: it diverges per surface
 *  (6, 8, 10 and a two-axis pair among the live carriers), so shipping one here would silently override every call
 *  site's own choice on the next spread.
 *
 *  RENAMED and RELOCATED here 2026-09-05 from NOTIFICATIONS_EMPTY_STATE_STYLE in `./style-primitives.ts`. The old
 *  name lied twice over: its own consumer `components/navigator/steps/AgentSelector.tsx` is a wizard step rather than a
 *  notification surface, and a `mfe/navigator/frontend` carrier of the same three members is a LOADING line rather than
 *  an empty state -- so neither qualifier in the old name survived a census of what the body carries. The leaf move is
 *  the other half of the same correction: `./style-primitives.ts` exists to break the circular import between
 *  badge-styles, this leaf and layout-styles.detail, and this preset is imported by none of those three, while living
 *  there forced its two members to be RESPELLED longhand rather than composed off {@link TEXT_SM_TERTIARY} (which lives
 *  here, so style-primitives cannot import it). */
export const TEXT_SM_TERTIARY_CENTERED: CSSProperties = {
	...TEXT_SM_TERTIARY,
	textAlign: 'center',
};

/** The centred SECONDARY run, at whatever size the consumer is already set in -- the sibling of
 *  {@link TEXT_SM_TERTIARY_CENTERED} one colour step up and with no `fontSize` of its own. Consumers spread it and add
 *  their own padding; `padding` stays out for the same reason it stays out of the tertiary sibling.
 *
 *  RENAMED 2026-09-05 from EMPTY_STATE_BASE, which was a ROLE name over a body that carries no role: the two members
 *  are a text colour and an alignment, and a census of its consumers then found one of them drawing a LOADING line
 *  rather than an empty state, exactly the mis-fit that had already renamed NOTIFICATIONS_EMPTY_STATE_STYLE into the
 *  tertiary sibling above. The new name states the body, so a third consumer drawing neither an empty state nor a
 *  loading line can adopt it without the name having to be re-litigated. No `as const` rides `textAlign`: the
 *  `CSSProperties` annotation already narrows the literal, which is why no sibling in this family spells one. */
export const TEXT_SECONDARY_CENTERED: CSSProperties = {
	textAlign: 'center',
	color: colors.text.secondary,
};

/** The small secondary run set on the RELAXED leading -- the multi-line body copy under a heading or beside a control,
 *  as opposed to the single-line label {@link TEXT_SM_SECONDARY} answers. Named because five surfaces had spelled its
 *  three members out longhand (the navigator wizard subtitle and info-card body, the notifications popover message, and
 *  both branches of the selection-card description), each then adding its OWN gap on top -- which is the composition
 *  `QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001` names, and it needs a base to compose. */
export const TEXT_SM_SECONDARY_RELAXED: CSSProperties = {
	...TEXT_SM_SECONDARY,
	lineHeight: typography.lineHeight.relaxed,
};

/** The small secondary run set on the NORMAL leading -- the sibling of {@link TEXT_SM_SECONDARY_RELAXED} at the other
 *  leading token, for the single-paragraph subtitle / description / body line that does not need the open leading of
 *  multi-line prose.
 *
 *  MINTED AT FIVE CARRIERS, which is the count that named the RELAXED sibling above, not a lowered bar: the page
 *  template subtitle and the navigator step description had spelled the pair out identically AND had copied the same
 *  two-line justification comment verbatim into both leaves, while the confirm-dialog description, the title-gate body
 *  and the noor connections card text each composed the same three members with one more of their own -- the
 *  compose-over-a-base shape `QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001` names, which needs a base to compose.
 *
 *  ALL FIVE ARE NOW DRAINED. The last one, `STEP_DESCRIPTION_STYLE` in `components/navigator/navigator-styles.ts`, was
 *  held by a concurrent lane when this was minted and is gone 2026-09-05; the second half of the duplicated
 *  justification comment went with it. Its two consumers took the base directly, and one of them -- the photos-step
 *  subtitle -- turned out to be a RELAXED carrier wearing the normal-leading base plus an immediate `lineHeight`
 *  override, so it now composes {@link TEXT_SM_SECONDARY_RELAXED} instead. */
export const TEXT_SM_SECONDARY_NORMAL: CSSProperties = {
	...TEXT_SM_SECONDARY,
	lineHeight: typography.lineHeight.normal,
};

export const TEXT_XS_MEDIUM: CSSProperties = {
	fontSize: typography.fontSize.xs,
	fontWeight: typography.fontWeight.medium,
};

/** The `xs` size + `medium` weight run in the SECONDARY colour -- the pill, chip, badge and small-label typography.
 *  Byte-for-byte the shape {@link TEXT_SM_MEDIUM_SECONDARY} already has one size step up in this leaf, so this is a
 *  rung on an existing family rather than a one-off.
 *
 *  MINTED ON A COUNT OF THIRTEEN, against this family's recorded naming bar of five: a tree-wide census on 2026-09-05
 *  found thirteen module-scope style consts carrying all three members longhand. Both halves of the pair that
 *  SPECIFIED this mint -- `TYPE_PILL_STYLE` (`apps/admin-federa/src/app/listings-review/page.row-list.tsx`) and
 *  `DESKTOP_COUNTER_BADGE_STYLE` (`apps/federa/src/components/property/PropertyGallery.layouts.tsx`) -- compose it as
 *  of this change, and their standing decline ("no single preset covers it": `TEXT_XS_MEDIUM` owns {size, weight} and
 *  `MUTED_LABEL_SECONDARY` owns {size, colour}, and they OVERLAP on `fontSize`, so spreading either strands the
 *  other's member inline) is retired by the mint that was its own stated falsifier.
 *
 *  THIS LEAF AND NO OTHER: composing it anywhere else would have to re-spell `TEXT_XS_MEDIUM`'s two members, since the
 *  import edge runs layout-styles -> style-primitives and not back, and a mint that respells the preset it is built
 *  from is the waste it exists to remove.
 *
 *  THIS LEAF IS DRAINED. The two carriers that sat HERE -- the xs field label (since deleted with its last reader)
 *  and {@link UPPERCASE_LABEL_STYLE} -- composed the preset as of that change, because a preset family's own leaf is
 *  the last place a respell may stand.
 *  The fold is queue-NEUTRAL for both sibling matchers it could have disturbed, measured rather than assumed: replayed
 *  over the 1,711 UI-tier leaves, `QUAL-COMPONENT-STYLE-CONST-DUPLICATE-001` and
 *  `QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001` each score 0 rows on the HEAD bytes and 0 on these. The duplicate
 *  rule's four-property FLOOR is the one thing the fold moves -- both bodies drop from five members to three -- and the
 *  coverage that floor was holding is not lost but widened: a longhand copy of either now fires
 *  `QUAL-STYLE-XS-MEDIUM-SECONDARY-SSOT-001`, which tests CONTAINMENT and so catches the copy-plus-extras shape the
 *  equality arm never could.
 *
 *  A CARRIER COMPOSES THIS PRESET, never re-spells it: it spreads the preset as its FIRST member and restates none of
 *  the three after the spread, so no member is silently overridden, and a body that is EXACTLY these three members
 *  imports the preset rather than aliasing it -- `const X = PRESET;` is itself a duplicate row
 *  (`QUAL-COMPONENT-STYLE-CONST-DUPLICATE-001`, zero-delta-alias arm). `QUAL-STYLE-XS-MEDIUM-SECONDARY-SSOT-001` fires
 *  on any new longhand carrier from day one; re-replay it before citing a count. */
export const TEXT_XS_MEDIUM_SECONDARY: CSSProperties = {
	...TEXT_XS_MEDIUM,
	color: colors.text.secondary,
};

export const TEXT_SM_SEMIBOLD: CSSProperties = {
	fontSize: typography.fontSize.sm,
	fontWeight: typography.fontWeight.semibold,
};

/** Slightly more prominent than MUTED_LABEL_STYLE. */
export const MUTED_LABEL_SECONDARY: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: colors.text.secondary,
};

/** Large font + semibold weight, primary color -- the canonical card/section title preset. */
export const TEXT_LG_SEMIBOLD_PRIMARY: CSSProperties = {
	fontSize: typography.fontSize.lg,
	fontWeight: typography.fontWeight.semibold,
	color: colors.text.primary,
};

export const HEADING_LG_SEMIBOLD: CSSProperties = {
	...TEXT_LG_SEMIBOLD_PRIMARY,
	margin: 0,
};

/** The FLUSH base-scale heading -- the `base` rung of the same pair `HEADING_LG_SEMIBOLD` occupies at `lg`. Named
 *  because three surfaces (the explore calendar's month title, the market-maker phase title and the admin
 *  source-group heading) had spelled its four members out longhand, and because the SPACED variant below is exactly
 *  this plus one margin.
 *
 *  A SECOND trio -- the notifications popover title, the photos-step empty-state title and the offers sheet/modal
 *  header title -- carried the same three typographic members WITHOUT `margin: 0` under three local names, and a
 *  2026-09-04 note declined the fold on the ground that zeroing the margin would be a render change "at any of the
 *  three that sits on a heading element". A 2026-09-05 re-census read the JSX: all three sit on `<span>`, whose UA
 *  margin is already zero, so the fold was byte-equivalent at every one and the three local declarations are gone. */
export const HEADING_BASE_SEMIBOLD: CSSProperties = {
	fontSize: typography.fontSize.base,
	fontWeight: typography.fontWeight.semibold,
	margin: 0,
	color: colors.text.primary,
};

/** The `h2` inside a bordered detail SECTION: the flush base heading above plus the gap to the block under it. The gap
 *  lives HERE rather than at the call sites so it stays one decision
 *  (`QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001`); the admin property-documents section and the market-maker ledger
 *  section had spelled the same five members out longhand under the same local name. */
export const HEADING_BASE_SEMIBOLD_SPACED: CSSProperties = {
	...HEADING_BASE_SEMIBOLD,
	marginBottom: spacing[3],
};

export const TEXT_SM_SEMIBOLD_PRIMARY: CSSProperties = {
	...TEXT_SM_SEMIBOLD,
	color: colors.text.primary,
};

/** TEXT_SM_SEMIBOLD plus the gap to the body text under it -- the h3-scale run in rendered markdown. The gap lives
 *  HERE rather than at the call site so it stays one decision (`QUAL-STYLE-BASE-MARGIN-ONLY-COMPOSITION-001`). */
export const TEXT_SM_SEMIBOLD_SPACED: CSSProperties = {
	...TEXT_SM_SEMIBOLD,
	marginBottom: spacing[1],
};

/** Medium weight, PRIMARY colour, NO font size -- the emphasised run that takes its scale from whatever it sits in: a
 *  metric's volume figure, a detail item's title, a bid amount, a history entry's timestamp, a document filename. The
 *  absent `fontSize` is the decision, not an omission; {@link TEXT_SM_MEDIUM_PRIMARY} below is this preset pinned to the
 *  small rung. Named because a census measured the two members hand-spelled at SIX sites in four packages under six
 *  local names, one of which (DETAIL_ITEM_TITLE, then in `components/table-kit/detail-panel-styles.ts`) had become the de
 *  facto shared export while its name claimed a detail-panel role it no longer had -- noor's history timestamps and the
 *  market-maker document rows read it too. */
export const TEXT_MEDIUM_PRIMARY: CSSProperties = {
	fontWeight: typography.fontWeight.medium,
	color: colors.text.primary,
};

export const TEXT_SM_MEDIUM_PRIMARY: CSSProperties = {
	...TEXT_MEDIUM_PRIMARY,
	fontSize: typography.fontSize.sm,
};

/** Small font + medium weight, secondary color -- the canonical form-label / selector-label preset. */
export const TEXT_SM_MEDIUM_SECONDARY: CSSProperties = {
	...TEXT_SM_MEDIUM,
	color: colors.text.secondary,
};

/** Muted card surface -- background, radius and border WITHOUT padding, so each card sets its own. */
export const CARD_SURFACE_MUTED: CSSProperties = {
	backgroundColor: colors.background.secondary,
	borderRadius: borderRadius.md,
	border: BORDER_MUTED,
};

export const CARD_PANEL_SM: CSSProperties = {
	...CARD_SURFACE_MUTED,
	padding: spacing[3],
};

// The label-over-control field stack left this leaf 2026-09-04 as `FLEX_COLUMN_GAP1_STYLE` in `./style-primitives.ts`,
// beside the rest of the gap ladder. Under its old name (`FIELD_STACK_STYLE`) it claimed the form-field role while a
// census found the same body carrying mastheads, hero groups and breakdown lists at thirteen further sites.

// `satisfies Record<ComponentSize, string>` (added 2026-09-04): `IconButton` and `SaveBookmarkButton` index this by a
// `ComponentSize`-typed `size`, so a MISSING rung was already an error while an EXTRA one left behind by a narrowing of
// that scale compiled forever. Keyed off the SCALE rather than off `IconButtonProps['size']`, because the scale is the
// vocabulary this leaf actually mirrors and the prop merely spends it.
export const ICON_BUTTON_SIZE_CLASSES = {
	sm: 'h-6 w-6',
	md: 'h-8 w-8',
	lg: 'h-10 w-10',
} as const satisfies Record<ComponentSize, string>;

/** Negates the Modal's default padding so content runs edge-to-edge. */
export const FULL_BLEED_MODAL_STYLE: CSSProperties = {
	margin: `-${spacing[6]}`,
	display: 'flex',
};

/** Full-viewport backdrop WITHOUT flex centering (drawer overlays). */
export const BACKDROP_BASE_STYLE: CSSProperties = {
	position: 'fixed',
	inset: 0,
	zIndex: zIndex.modalBackdrop,
};

export const OVERLAY_BASE_STYLE: CSSProperties = {
	...BACKDROP_BASE_STYLE,
	...FLEX_CENTER_STYLE,
};

export const PANEL_HEADER_BASE_STYLE: CSSProperties = {
	...FLEX_BETWEEN_STYLE,
	borderBottom: BORDER_MUTED,
};

// The panel/modal/drawer title is `HEADING_LG_SEMIBOLD` above and nothing else; Modal, DrawerHeader and ConfirmDialog
// spread it directly. A longhand twin of it in this leaf would be one decision wearing two names in one file, and
// `QUAL-COMPONENT-STYLE-CONST-DUPLICATE-001`'s intra-leaf-twin arm would not see it: the arm compares SOURCE bodies at a
// four-top-level-property floor, and this heading's body is a two-member spread composition
// (`{ ...TEXT_LG_SEMIBOLD_PRIMARY, margin: 0 }`), so such a pair is neither byte-equal nor over the floor.

export const PANEL_FOOTER_BASE_STYLE: CSSProperties = {
	display: 'flex',
	justifyContent: 'flex-end',
	gap: spacing[3],
};

export const MONO_FONT_STYLE: CSSProperties = {
	fontFamily: typography.fontFamily.mono,
};

/** The uppercase eyebrow: the shared {@link TEXT_XS_MEDIUM_SECONDARY} run plus the two members that make it uppercase.
 *  The pair below it ({@link UPPERCASE_LABEL_SEMIBOLD_STYLE}) already composed THIS const rather than respelling it, so
 *  the family now runs base -> eyebrow -> heavy eyebrow with each rung adding only what its name says. */
export const UPPERCASE_LABEL_STYLE: CSSProperties = {
	...TEXT_XS_MEDIUM_SECONDARY,
	textTransform: 'uppercase',
	letterSpacing: typography.letterSpacing.uppercase,
};

/** {@link UPPERCASE_LABEL_STYLE} at the heavier weight -- the uppercase eyebrow that titles a block rather than
 *  labelling a field. Two surfaces (the metadata grid's `uppercase` variant and the flow viewer's condition title) had
 *  each declared this one-member override privately under a different name. */
export const UPPERCASE_LABEL_SEMIBOLD_STYLE: CSSProperties = {
	...UPPERCASE_LABEL_STYLE,
	fontWeight: typography.fontWeight.semibold,
};

export const SECTION_PADDING_STYLE: CSSProperties = { padding: spacing[6] };

/**
 * THE PANEL/CARD FRAME RADIUS, as a style object rather than as a token rung re-typed at every frame. `2lg` is the
 * corner the property page's panels agree on -- the gallery hero, the tab strip, the fact grid, the quick-facts panel,
 * the auction-terms card, the title panel and the trade CTA aside -- and until this mint that agreement was spelled
 * nowhere and asserted 24 times.
 *
 * THIS LEAF AND NO OTHER. `borderRadius` here is the `../css-refs.effects.ts` token ref, so a `const/style-*-presets.ts`
 * leaf beside `FULL_WIDTH_STYLE` and `FLEX_BETWEEN_STYLE` CANNOT be the home -- `const/` may not import a `components/`
 * artifact. The one reachable home is inside `components/`, and this leaf is where the fleet's shared style objects
 * already live.
 *
 * WHY A ONE-MEMBER PRESET IS WORTH A NAME. The member is not the waste; the DECISION is. `borderRadius['2lg']` names a
 * rung on the radius scale, which answers "how round", while this preset names "the panel frame's corner", which is
 * what a retune would actually be retuning. A frame that wants a different corner stops spreading this and says so.
 *
 * CENSUS 2026-09-05 over the tracked ts/tsx tree, named rather than counted so the drain is a work list: `borderRadius['2lg']`
 * was spelled at 24 sites in four layers -- apps/ (8), components/ (5), mfe/ (5), spa/ (8). SEVEN apps/ sites and ONE
 * mfe/ site adopted this preset in the minting change, including the two BYTE-IDENTICAL bodies that sat under different
 * names in one directory (DESKTOP_HERO_FRAME_STYLE in `PropertyGallery.layouts.tsx`, `PANEL_RADIUS_STYLE` in
 * `PropertyTitleTab.tsx`) and ASIDE_FRAME_STYLE (`mfe/trade/frontend/src/PropertyCtaPanel.tsx`) -- all three deleted
 * outright rather than aliased, since `const X = PRESET;` is itself a duplicate row
 * (`QUAL-COMPONENT-STYLE-CONST-DUPLICATE-001`, zero-delta-alias arm).
 *
 * THE REMAINDER IS NOT THIS PRESET AND IS NOT OWED TO IT, which is the reason the fold stops at eight. Every other
 * carrier binds the rung to a DIFFERENT property or composes it: corner-specific radii
 * (`mfe/navigator/.../MyPropertiesView.card.styles.ts`, four), a two-corner template literal
 * (`components/domain-cards/PropertyCard.carousel.tsx`), a `width`/`height` (`components/explore/ExploreSearch.components.tsx`,
 * two), a `radius` PROP (`components/connect/kit/Feedback.tsx`), the Satori OG route whose `borderRadius` comes from
 * `@federa/design-system/tokens/borders` because an image renderer has no CSS custom properties
 * (`apps/federa/src/app/api/og/route.tsx`), and SIXTEEN bodies carrying the member among their OWN chrome (recounted
 * 2026-09-05 after the eight adoptions landed; the pre-fold reading of this same class was eleven). That last class is
 * a CONTAINMENT question, and containment at a ONE-member floor is the shape this leaf already declines four
 * times over at two members (`INPUT_BASE_STYLE` 2/7, `FILTER_INPUT` 2/9, `ITEM` 2/9, `menuButtonStyles` 2/7). A rule
 * over THAT arm would therefore be a measured false positive, exactly as `QUAL-STYLE-XS-MEDIUM-SECONDARY-SSOT-001`
 * records for its own containment arm below three members.
 *
 * A TWO-MEMBER PRESET OVER THAT CONTAINMENT CLASS IS DECLINED ON A TALLY, taken over all sixteen carriers rather than
 * on the doctrine above, so the decline carries its own falsifier. The SECOND member each body pairs with the rung:
 * `backgroundColor: colors.background.primary` at FOUR (`getCardStyles` in `components/domain-cards/PropertyCard.tsx`,
 * `POPOVER_STYLE` and `WHERE_INPUT_WRAPPER_STYLE` in spa/explore's DesktopFilterBar pair, `CARD_STYLE` in
 * `mfe/navigator/.../MyPropertiesView.card.styles.ts`), `boxShadow: boxShadow.lg` at THREE, `border: BORDER_MUTED` at
 * THREE, `fontSize: typography.fontSize.sm` at THREE. FOUR different second members each clear a three-site floor and
 * NO THREE carriers agree on the same one -- the signature of two independent token choices co-occurring, not of one
 * decision with two members. Minting any one pair would convict the carriers of the other three of failing to adopt it.
 * Two grounds outlive the tally. ONE of the sixteen sits in apps/ and NONE in brand/, so a mint would ship with no
 * consumer in the lane that owns this home -- and that one, `TAB_PILL_BASE_STYLE`
 * (`apps/federa/src/components/property/PropertyDetailsTabs.tsx`), already records a decline naming a PILL preset
 * rather than a panel one as its falsifier. And the fill is not an inline decision in this lane at all: all SEVEN JSX
 * consumers of this preset across apps/ and mfe/ paint the surface with the Tailwind `bg-background` class, so a
 * `{ borderRadius, backgroundColor }` preset would FORK the fill spelling rather than home it. FALSIFIER: three
 * carriers sharing the same second member, at least one of them in apps/ or brand/ with its fill spelled inline.
 *
 * THE PAIRED FORBID-RULE FOR THE EQUALITY ARM IS LANDED: `QUAL-STYLE-PANEL-FRAME-RADIUS-SSOT-001`, ACTIVE at HIGH from
 * day one. The FP-free arm is EQUALITY, not containment: an object literal whose ONLY member is this preset's only
 * member IS this preset, and its cure is byte-equivalent. Its day-one queue is ZERO -- a replay of that matcher over
 * every tracked ts/tsx leaf under apps/, brand/, components/, mfe/ and spa/ scores 0 rows, this declaration included --
 * and the zero is a DRAINED queue rather than a disarmed matcher, measured: the same matcher replayed against the
 * pre-fold bytes at b9ee9d45b3f~1 scores exactly the three twins folded above. HIGH rather than the MEDIUM day-one
 * valve precisely because there is no queue for a concession to buy time for.
 *
 * WHAT IT TOOK, recorded because the blocker was real and its clearance is the reusable part. The matcher must DERIVE
 * the member from this leaf rather than hard-code it (`.claude/skills/compliance-rule-authoring/SKILL.md`
 * `## Runtime-derived SSOT-bypass rules (authoring pattern)`); the one helper that already performs that derivation was
 * named `readTypographyPreset` in `typography-preset-respell.ts`, and adding a non-typography consumer to it would have
 * made its NAME lie. A SIBLING helper beside it was the other offer and stays DECLINED: it would respell
 * `discoverStyleSsotPaths` -> module-scope-decl scan -> intra-leaf spread resolution, which is the exact walk that
 * helper exists to own, so the cure for one duplicated decision would have minted another. The rename landed instead --
 * `utils/compliance/rule-helpers/style-preset-respell.ts`, `readStylePreset` / `StylePreset` -- with an EQUALITY
 * collector added beside `collectPresetRespellLocations` and both detectors sharing one acquittal block.
 *
 * THE SCOPE THE EQUALITY RULE DECLINES, measured rather than assumed, so a later widen has a falsifier to beat: a
 * `style={{ ... }}` JSX attribute and a render-local `const` are NOT read, on the same ground the containment sibling
 * declines them -- their cure is the same import, but their declaration is not the decision. A tree-wide probe over the
 * four UI tiers on 2026-09-05 found ZERO carriers of either shape for this preset. FALSIFIER: a JSX-attribute or
 * render-local body that IS a discovered preset. */
export const PANEL_FRAME_RADIUS_STYLE: CSSProperties = { borderRadius: borderRadius['2lg'] };

export const CARD_BASE_STYLE: CSSProperties = {
	backgroundColor: colors.background.primary,
	borderRadius: borderRadius.md,
};

export const ELEVATED_CARD_STYLE: CSSProperties = {
	...CARD_BASE_STYLE,
	borderRadius: borderRadius.lg,
	padding: spacing[6],
	boxShadow: boxShadow.sm,
};

/** For prominent action buttons (Save, Deposit); extend with radius/colors. */
export const ACTION_BUTTON_PADDING_STYLE: CSSProperties = {
	padding: `${spacing[3]} ${spacing[6]}`,
	fontWeight: typography.fontWeight.medium,
};

/**
 * HOW FAR A DISABLED CONTROL DIMS, and the one place that decides it.
 *
 * The ternary was spelled at SIX sites in this package -- {@link getDisabledInteractiveStyle} below, `Button`,
 * `Select`, both `Input` style builders and the choice controls -- and every copy spelled the enabled arm as a bare
 * `1`, which is the SDL bypass `DS-HARDCODED-STYLES-002` names: `--opacity-opaque` is a declared brand token with a
 * `css-refs` reader (`opacity.opaque`) that four surfaces outside this package already take. The disabled arm binds
 * `disabledTokens.opacity` (const-layer `opacity.muted`), so the two arms come from the two layers that own them and
 * neither is a literal. The five controls that dim WITHOUT also setting cursor and transition take this rather than
 * the fuller helper below, which is why the pair is split rather than folded.
 */
export function disabledOpacity(disabled: boolean | undefined): CSSProperties['opacity'] {
	// `undefined` is admitted rather than pushed onto the callers: four of the six adopters take `disabled` straight off
	// an optional prop, and a `?? false` at each of them would be this coercion spelled four times.
	return disabled === true ? disabledTokens.opacity : opacity.opaque;
}

export function getDisabledInteractiveStyle(disabled: boolean): CSSProperties {
	return {
		cursor: disabled ? disabledTokens.cursor : cursor.pointer,
		opacity: disabledOpacity(disabled),
		transition: `all ${animations.fast}ms ${motion.easing.standard}`,
	};
}

/** The header bar's fixed height, typed as a string so a `calc()` offsetting content below the bar can spell it. */
export const HEADER_BAR_HEIGHT = spacing[16];

/** Fixed-height header bar; apply borderColor separately (varies by context). */
export const HEADER_BAR_BASE_STYLE: CSSProperties = {
	padding: `0 ${spacing[4]}`,
	height: HEADER_BAR_HEIGHT,
};

export const GHOST_BUTTON_BASE: CSSProperties = {
	backgroundColor: 'transparent',
	border: 'none',
	cursor: cursor.pointer,
};

export const SOLID_BUTTON_BASE: CSSProperties = {
	border: 'none',
	cursor: cursor.pointer,
};

/** Single-token utilities are INTENTIONALLY excluded -- the tokens ARE the SSOT. Only add multi-token strings with >= 4 cross-file callsites and a stable structural meaning. */
export const LAYOUT_CLASSES = {
	rowBetween: 'flex items-center justify-between',
	rowBetweenTop: 'flex items-start justify-between',
	rowGap2: 'flex items-center gap-2',
	row: 'flex items-center',
	centerAll: 'flex items-center justify-center',
	columnCenterAll: 'flex flex-col items-center justify-center',
	column: 'flex flex-col',
	columnGap6: 'flex flex-col gap-6',
	columnGap3: 'flex flex-col gap-3',
	desktopOnly: 'hidden md:block',
	/** An app's full-height row: its menu beside the one main landmark, which scrolls on its own (`scrollPane`). */
	appFrame: 'flex h-screen overflow-hidden',
	scrollPane: 'flex-1 overflow-y-auto',
	flexFillTruncate: 'flex-1 min-w-0',
} as const;

/** Tailwind class for a large section heading (`<h2>`/`<h3>` above a property/detail content block).
 *  Deliberately NOT the class twin of `HEADING_LG_SEMIBOLD`, which uses the primary text color and zeroes margin. */
export const SECTION_HEADING_LG_CLASS = 'text-lg font-semibold text-secondary';

/** Border-less variant is `LAYOUT_CLASSES.rowBetween`. */
export const MODAL_HEADER_ROW_CLASS = `${LAYOUT_CLASSES.rowBetween} border-b`;
export const MODAL_HEADER_ROW_MUTED_CLASS = `${MODAL_HEADER_ROW_CLASS} border-muted`;
